import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { selectConfigurationParameterById } from '!core/redux';
import { Questionnaire } from '»components/Questionnaire';
import { questionnaireSlice } from '»components/Questionnaire/redux';
import { disclamerQuestionnaireSelector } from '»components/Questionnaire/redux/questionnaire.selectors';
import { isAureaActiveClient, isHemeraActiveClient } from '»utils/clientsUtils';
import { useCurrentPage } from '»utils/customHooks';

import { AuthWrapper } from '../AuthWrapper';
import { authSlice } from '../redux';
import { CreateAccount } from './CreateAccount/index.js';
import { DocumentAcceptance } from './DocumentAcceptance';
import { FilesAttachment } from './FilesAttachment';
import { MainForm, PartnersForm, RepreForm } from './MainForm';
import { OnboardingInitialInfo } from './OnboardingInitialInfo';
import { ProcessValidation } from './ProcessValidation';
import {
  onboardingCurrentStepSelector,
  onboardingErrorSelector,
  onboardingLegalStatusSelector,
  onboardingSlice,
} from './redux';
import { userInitialOtpDelivMetdsSelector } from './redux/onboarding.selectors';

export const Onboarding = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isOnboardingAdvancedSteps } = useCurrentPage(location.pathname);

  const onboardingCurrentStep = useSelector(onboardingCurrentStepSelector);
  const onboardingError = useSelector(onboardingErrorSelector);
  const legalStatus = useSelector(onboardingLegalStatusSelector);
  const disclamerQuestionnaire = useSelector(disclamerQuestionnaireSelector);
  const onboardingInitialOtpDelivMetds = useSelector(
    userInitialOtpDelivMetdsSelector,
  )?.split(';');
  const onboardingFinalOtpDelivMetds = useSelector((state) =>
    selectConfigurationParameterById(
      state,
      'ONBOARDING_OTP_DELIVERY_METHODS_END',
    ),
  ).value.split(';');

  const hasOneInitialOtpDelivMetd =
    onboardingInitialOtpDelivMetds?.length === 1;

  const hasOneFinalOtpDelivMetd = onboardingFinalOtpDelivMetds?.length === 1;

  const NEW_ACCOUNT_EXP = isAureaActiveClient
    ? 'TRLT_UI-OPEN_ACCOUNT'
    : 'TRLT_UI-CREATE_ACCOUNT';

  const LOGIN_EXP = isAureaActiveClient
    ? 'TRLT_UI-ACCESS_ACCOUNT'
    : 'TRLT_UI-LOGIN';

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isOnboardingAdvancedSteps) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    const handleUnload = () => {
      dispatch(authSlice.actions.resetAuth());
      dispatch(questionnaireSlice.actions.clearQuestionnaire());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingAdvancedSteps]);

  const clearOnboardingError = () => {
    dispatch(onboardingSlice.actions.clearOnboardingError());
  };

  const getCurrentStepComponent = () => {
    switch (onboardingCurrentStep) {
      case 0:
        return <OnboardingInitialInfo />;
      case 1:
        return <CreateAccount />;
      case 2:
        return <ProcessValidation />;
      case 3:
        return <MainForm />;
      case 4:
        return <PartnersForm />;
      case 5:
        return <RepreForm />;
      case 6:
        return <Questionnaire />;
      case 7:
        return <DocumentAcceptance />;
      case 8:
        return <FilesAttachment />;
      case 9:
        return <ProcessValidation />;
      default:
        return null;
    }
  };

  const getScreenSummary = (summaryElementName) => {
    let elementContent = null;

    switch (onboardingCurrentStep) {
      case 0:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-STINVEST_ACCOUNT';
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-STINVEST_ACCOUNT_DESCR';
        } else if (summaryElementName === 'secondDescr') {
          elementContent = 'TRLT_UI-ALREADY_CLIENT';
        } else if (summaryElementName === 'linkText') {
          elementContent = LOGIN_EXP;
        } else if (summaryElementName === 'linkRoute') {
          elementContent = '/Login';
        } else if (summaryElementName === 'linkMenuItem') {
          elementContent = 'LOGIN';
        }

        break;
      case 1:
        if (summaryElementName === 'title') {
          elementContent = NEW_ACCOUNT_EXP;
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-ONBOARDING_INFO_TRADING';
        } else if (summaryElementName === 'secondDescr') {
          elementContent = 'TRLT_UI-ALREADY_CLIENT';
        } else if (summaryElementName === 'linkText') {
          elementContent = LOGIN_EXP;
        } else if (summaryElementName === 'linkRoute') {
          elementContent = '/Login';
        } else if (summaryElementName === 'linkMenuItem') {
          elementContent = 'LOGIN';
        }

        break;
      case 2:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-MESSAGE_VALIDATION';
        } else if (summaryElementName === 'descr') {
          elementContent = hasOneInitialOtpDelivMetd
            ? 'TRLT_UI-MESSAGE_VALIDATION_SEC_DESCR'
            : 'TRLT_UI-MESSAGE_VALIDATION_DESCR';
        }

        break;
      case 3:
        if (summaryElementName === 'title') {
          elementContent =
            legalStatus === 'PS'
              ? 'TRLT_UI-NATURAL_PERSON'
              : legalStatus === 'PC' && 'TRLT_UI-LEGAL_PERSON';
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-FILL_IN_FORM';
        }

        break;
      case 4:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-PARTNERS';
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-FILL_IN_FORM';
        }

        break;
      case 5:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-REPRS_BENEFS';
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-FILL_IN_FORM';
        }

        break;
      case 6:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-INVEST_PROF_QUEST';
        } else if (summaryElementName === 'descr') {
          elementContent = disclamerQuestionnaire || '';
        }

        break;
      case 7:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-DOCUMENTS';
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-DOCUMENTS';
        }

        break;
      case 8:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-UPLOAD_DOCUMENTS';
        } else if (summaryElementName === 'descr') {
          elementContent = 'TRLT_UI-UPLOAD_DOCS_DESCR';
        } else if (
          summaryElementName === 'screenNotice' &&
          isHemeraActiveClient
        ) {
          if (legalStatus === 'PS') {
            elementContent = 'TRLT_UI-UPLOAD_DOCS_NOTICE';
          } else {
            elementContent = 'TRLT_UI-UPLOAD_DOCS_NOTICE_PC';
          }
        }

        break;
      case 9:
        if (summaryElementName === 'title') {
          elementContent = 'TRLT_UI-MESSAGE_VALIDATION';
        } else if (summaryElementName === 'descr') {
          elementContent = hasOneFinalOtpDelivMetd
            ? 'TRLT_UI-MESSAGE_VALIDATION_SEC_DESCR'
            : 'TRLT_UI-MESSAGE_VALIDATION_DESCR';
        }

        break;
      default:
        break;
    }

    return elementContent;
  };

  return (
    <AuthWrapper
      screenTitle={getScreenSummary('title')}
      screenDescr={getScreenSummary('descr')}
      screenSecondDescr={getScreenSummary('secondDescr')}
      screenNotice={getScreenSummary('screenNotice')}
      linkText={getScreenSummary('linkText')}
      linkRoute={getScreenSummary('linkRoute')}
      linkMenuItem={getScreenSummary('linkMenuItem')}
      showFeedbackMsg={!!onboardingError}
      feedbackMsg={onboardingError}
      handleCloseFeedback={clearOnboardingError}
    >
      {getCurrentStepComponent()}
    </AuthWrapper>
  );
};
